import { Link } from 'react-router-dom';
import { Lock, CreditCard, CheckCircle, AlertCircle, Shield } from 'lucide-react';
import { Button } from '@shared/components/ui/Button';
import { useAccessControl } from '../hooks/useAccessControl';

export type PromptType = 'setup_fee' | 'subscription_needed' | 'setup_complete_need_subscription' | 'upgrade';

interface SubscriptionPromptProps {
  type?: PromptType;
  featureName?: string;
  className?: string;
  intendedTier?: string | null;
  onSubscribeClick?: () => void;
  showIfAdmin?: boolean;
}

export function SubscriptionPrompt({
  type = 'setup_fee',
  featureName,
  className = '',
  intendedTier,
  onSubscribeClick,
  showIfAdmin = false,
}: SubscriptionPromptProps) {
  const { isAdmin, needsSetupFee, needsSubscription, loading } = useAccessControl();

  if (loading) {
    return (
      <div className={`flex items-center justify-center py-4 ${className}`}>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (isAdmin && !showIfAdmin) {
    return null;
  }

  if (isAdmin && showIfAdmin) {
    return (
      <div className={`mb-8 bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300 rounded-xl p-6 shadow-md ${className}`}>
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center">
            <Shield className="w-8 h-8 text-green-600" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-green-900 mb-1">Admin Access</h2>
            <p className="text-green-800">
              You have full administrative access to all features.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (type === 'setup_fee' && needsSetupFee) {
    return (
      <div className={`mb-8 bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-300 rounded-xl p-6 shadow-md ${className}`}>
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center">
              <Lock className="w-8 h-8 text-amber-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-amber-900 mb-1">Unlock Full Access</h2>
              <p className="text-amber-800">
                Complete your one-time setup payment to access all features including Career Reports, AI Coach, and Job Match AI
              </p>
            </div>
          </div>
          <Link to="/subscription">
            <Button className="bg-amber-600 text-white hover:bg-amber-700 whitespace-nowrap">
              <CreditCard className="w-5 h-5 mr-2" />
              View Plans
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  if (type === 'setup_complete_need_subscription' && !needsSetupFee && needsSubscription && intendedTier) {
    return (
      <div className={`mb-8 bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300 rounded-xl p-6 shadow-md ${className}`}>
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-green-900 mb-1">Setup Payment Complete!</h2>
              <p className="text-green-800">
                Your $197 setup fee is paid. Now select your monthly {intendedTier} subscription plan to activate your account.
              </p>
            </div>
          </div>
          {onSubscribeClick ? (
            <Button
              onClick={onSubscribeClick}
              className="bg-green-600 text-white hover:bg-green-700 whitespace-nowrap"
            >
              <CreditCard className="w-5 h-5 mr-2" />
              Complete Subscription
            </Button>
          ) : (
            <Link to="/subscription">
              <Button className="bg-green-600 text-white hover:bg-green-700 whitespace-nowrap">
                <CreditCard className="w-5 h-5 mr-2" />
                Complete Subscription
              </Button>
            </Link>
          )}
        </div>
      </div>
    );
  }

  if (type === 'subscription_needed' && !needsSetupFee && needsSubscription) {
    return (
      <div className={`mb-8 bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-300 rounded-xl p-6 shadow-md ${className}`}>
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center">
              <AlertCircle className="w-8 h-8 text-blue-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-blue-900 mb-1">Subscription Required</h2>
              <p className="text-blue-800">
                {featureName ? `${featureName} requires an active subscription.` : 'An active subscription is required to access this feature.'}
              </p>
            </div>
          </div>
          <Link to="/subscription">
            <Button className="bg-blue-600 text-white hover:bg-blue-700 whitespace-nowrap">
              <CreditCard className="w-5 h-5 mr-2" />
              Subscribe Now
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  if (type === 'upgrade') {
    return (
      <div className={`mb-8 bg-gradient-to-r from-indigo-50 to-purple-50 border-2 border-indigo-300 rounded-xl p-6 shadow-md ${className}`}>
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-indigo-100 rounded-full flex items-center justify-center">
              <Lock className="w-8 h-8 text-indigo-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-indigo-900 mb-1">Premium Feature</h2>
              <p className="text-indigo-800">
                {featureName ? `${featureName} is available on higher tier plans.` : 'Upgrade your subscription to access this feature.'}
              </p>
            </div>
          </div>
          <Link to="/subscription">
            <Button className="bg-indigo-600 text-white hover:bg-indigo-700 whitespace-nowrap">
              Upgrade Plan
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return null;
}

interface InlineSubscriptionPromptProps {
  featureName?: string;
  compact?: boolean;
}

export function InlineSubscriptionPrompt({
  featureName = 'This feature',
  compact = false,
}: InlineSubscriptionPromptProps) {
  const { isAdmin, needsSetupFee } = useAccessControl();

  if (isAdmin) {
    return null;
  }

  if (compact) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-center">
        <p className="text-sm text-amber-800 mb-2">
          {featureName} requires a subscription.
        </p>
        <Link to="/subscription">
          <Button size="sm" className="bg-amber-600 text-white hover:bg-amber-700">
            {needsSetupFee ? 'View Plans' : 'Subscribe'}
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-lg p-6 text-center">
      <div className="inline-flex items-center justify-center w-12 h-12 bg-amber-100 rounded-full mb-3">
        <Lock className="text-amber-600" size={24} />
      </div>
      <h3 className="text-lg font-bold text-gray-900 mb-2">
        {featureName} Requires Access
      </h3>
      <p className="text-gray-600 mb-4 text-sm">
        {needsSetupFee
          ? 'Complete your one-time setup to unlock all features.'
          : 'Subscribe to access this feature and more.'}
      </p>
      <Link to="/subscription">
        <Button className="bg-amber-600 text-white hover:bg-amber-700">
          {needsSetupFee ? 'View Plans' : 'Subscribe Now'}
        </Button>
      </Link>
    </div>
  );
}
